/**
 * @fileoverview Card displaying matched external product IDs (aliases) for a variant.
 * @module features/products/components/MatchedProductIdsCard
 */

import { useState, useRef, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, AlertCircle, X, Undo2, LoaderCircle, ChevronDown, ChevronUp } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Money } from '@/shared/components/ui/Money'
import {
  useVariantResolutionStatusQuery,
  type SkuResolutionAlias,
} from '@/features/sku-alias/api/resolutionStatus'
import {
  useDeleteSkuAliasMutation,
  useCreateSkuAliasMutation,
} from '@/features/sku-alias/api/aliases'
import {
  masterVariantIdSchema,
  type SkuAliasId,
  type SkuAliasSource,
} from '@/features/sku-alias/schemas/skuAliasSchema'

interface MatchedProductIdsCardProps {
  /** The selected variant ID to display aliases for */
  variantId: number | null
  /** Optional variant name for empty state context */
  variantName?: string | undefined
}

/**
 * Groups aliases by slug and computes display sources.
 * When a slug has multiple sources, prefer showing real external sources over 'canonical'.
 */
interface GroupedAlias {
  /** The slug (external ID) */
  slug: string
  /** All sources for this slug */
  sources: SkuAliasSource[]
  /** Sources to display (excludes 'canonical' if other sources exist) */
  displaySources: SkuAliasSource[]
  /** Representative alias (first non-canonical, or the canonical one) */
  representativeAlias: SkuResolutionAlias
  /** All alias IDs with this slug */
  aliasIds: SkuAliasId[]
}

function groupAliasesBySlug(aliases: SkuResolutionAlias[]): GroupedAlias[] {
  const slugMap = new Map<string, SkuResolutionAlias[]>()

  for (const alias of aliases) {
    const existing = slugMap.get(alias.slug) ?? []
    existing.push(alias)
    slugMap.set(alias.slug, existing)
  }

  const grouped: GroupedAlias[] = []
  for (const [slug, aliasesForSlug] of slugMap) {
    const sources = aliasesForSlug.map((a) => a.source)
    const nonCanonicalSources = sources.filter((s) => s !== 'canonical')

    // If there are non-canonical sources, display only those; otherwise show canonical
    const displaySources = nonCanonicalSources.length > 0 ? nonCanonicalSources : sources

    // Pick a representative alias (prefer non-canonical)
    const representativeAlias =
      aliasesForSlug.find((a) => a.source !== 'canonical') ?? aliasesForSlug[0]!

    grouped.push({
      slug,
      sources,
      displaySources,
      representativeAlias,
      aliasIds: aliasesForSlug.map((a) => a.id),
    })
  }

  return grouped
}

/**
 * Card component displaying matched external product IDs (aliases) as a table.
 * Supports deleting aliases with undo functionality.
 *
 * @component
 * @example
 * ```tsx
 * <MatchedProductIdsCard variantId={123} variantName="Blue Widget" />
 * ```
 */
export function MatchedProductIdsCard({
  variantId,
  variantName,
}: MatchedProductIdsCardProps): ReactElement | null {
  const { t } = useTranslation('features-skus')

  // Collapsed/expanded state - persist in localStorage
  const storageKey = 'matchedProductIds.expanded'
  const [isExpanded, setIsExpanded] = useState(() => {
    const stored = localStorage.getItem(storageKey)
    return stored === 'true'
  })

  const toggleExpanded = (): void => {
    setIsExpanded((prev) => {
      const next = !prev
      localStorage.setItem(storageKey, String(next))
      return next
    })
  }

  // Delete confirmation dialog state
  const [aliasToDeleteState, setAliasToDeleteState] = useState<{
    alias: SkuResolutionAlias | null
    variantId: number | null
  }>({ alias: null, variantId })
  const aliasToDeleteRef = useRef<SkuResolutionAlias | null>(null)

  // Track pending operations with variantId context
  const [pendingState, setPendingState] = useState<{
    aliasId: SkuAliasId | null
    undoId: SkuAliasId | null
    variantId: number | null
  }>({ aliasId: null, undoId: null, variantId })

  // Track removed aliases for undo functionality with variantId context
  const [removedAliasesState, setRemovedAliasesState] = useState<{
    map: Map<SkuAliasId, SkuResolutionAlias>
    variantId: number | null
  }>({ map: new Map(), variantId })

  // Derive current values - reset if variantId changed
  // This avoids cascading renders from setState in useEffect
  const aliasToDelete =
    aliasToDeleteState.variantId === variantId ? aliasToDeleteState.alias : null
  const pendingAliasId =
    pendingState.variantId === variantId ? pendingState.aliasId : null
  const pendingUndoId =
    pendingState.variantId === variantId ? pendingState.undoId : null
  const removedAliases =
    removedAliasesState.variantId === variantId ? removedAliasesState.map : new Map()

  // State update helpers that include current variantId
  const setAliasToDelete = (alias: SkuResolutionAlias | null): void => {
    setAliasToDeleteState({ alias, variantId })
  }

  const setPendingAliasId = (aliasId: SkuAliasId | null): void => {
    setPendingState((prev) => ({ ...prev, aliasId, variantId }))
  }

  const setPendingUndoId = (undoId: SkuAliasId | null): void => {
    setPendingState((prev) => ({ ...prev, undoId, variantId }))
  }

  const setRemovedAliases = (
    updater: Map<SkuAliasId, SkuResolutionAlias> | ((prev: Map<SkuAliasId, SkuResolutionAlias>) => Map<SkuAliasId, SkuResolutionAlias>)
  ): void => {
    setRemovedAliasesState((prev) => {
      const currentMap = prev.variantId === variantId ? prev.map : new Map()
      const newMap = typeof updater === 'function' ? updater(currentMap) : updater
      return { map: newMap, variantId }
    })
  }

  const {
    data: resolutionStatus,
    isLoading,
    isError,
    error,
    refetch,
  } = useVariantResolutionStatusQuery(variantId, {
    enabled: variantId !== null,
  })

  const deleteMutation = useDeleteSkuAliasMutation(variantId)
  const createMutation = useCreateSkuAliasMutation(variantId)

  const handleDeleteClick = (alias: SkuResolutionAlias): void => {
    aliasToDeleteRef.current = alias
    setAliasToDelete(alias)
  }

  const confirmDelete = (): void => {
    const alias = aliasToDeleteRef.current
    if (!alias) return

    const aliasId = alias.id
    const aliasData = alias

    setPendingAliasId(aliasId)
    deleteMutation.mutate(aliasId, {
      onSuccess: () => {
        setPendingAliasId(null)
        setRemovedAliases((prev) => {
          const next = new Map(prev)
          next.set(aliasId, aliasData)
          return next
        })
        toast.success(
          t('aliases.notifications.deleted', {
            defaultValue: 'Alias removed successfully.',
          })
        )
        aliasToDeleteRef.current = null
        setAliasToDelete(null)
      },
      onError: (err: Error) => {
        setPendingAliasId(null)
        const description =
          err.message ||
          t('aliases.errors.genericDetail', { defaultValue: 'Unknown error' })
        toast.error(
          t('aliases.errors.generic', { defaultValue: 'Failed to delete alias.' }),
          { description }
        )
        aliasToDeleteRef.current = null
        setAliasToDelete(null)
      },
      onSettled: () => {
        setPendingAliasId(null)
      },
    })
  }

  const handleUndo = (alias: SkuResolutionAlias): void => {
    if (variantId === null) return

    const masterVariantId = masterVariantIdSchema.parse(variantId)
    setPendingUndoId(alias.id)
    createMutation.mutate(
      { aliasSlug: alias.slug, source: alias.source, masterVariantId },
      {
        onSuccess: () => {
          setPendingUndoId(null)
          setRemovedAliases((prev) => {
            const next = new Map(prev)
            next.delete(alias.id)
            return next
          })
          toast.success(
            t('aliases.notifications.restored', { defaultValue: 'Alias restored.' })
          )
        },
        onError: (err) => {
          setPendingUndoId(null)
          const description =
            err instanceof Error
              ? err.message
              : t('aliases.errors.genericDetail', { defaultValue: 'Unknown error' })
          toast.error(
            t('aliases.errors.restore', { defaultValue: 'Failed to restore alias.' }),
            { description }
          )
        },
      }
    )
  }

  const liveAliases = resolutionStatus?.aliases ?? []
  const totals = resolutionStatus?.totals

  // Combine live aliases with removed-only entries for undo
  const removedOnlyEntries: SkuResolutionAlias[] = []
  removedAliases.forEach((alias) => {
    if (!liveAliases.some((item) => item.id === alias.id)) {
      removedOnlyEntries.push(alias)
    }
  })
  const allAliases = [...liveAliases, ...removedOnlyEntries]

  // Group aliases by slug, compute display sources, and track removed state
  const groupedAliases = groupAliasesBySlug(allAliases).map((group) => {
    // Check if ALL aliases in this group are removed
    const allRemoved = group.aliasIds.every((id) => removedAliases.has(id))
    return { ...group, removed: allRemoved }
  })

  const renderContent = (): ReactElement => {
    // Loading state
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <p className="text-sm">{t('messages.loading', { defaultValue: 'Loading...' })}</p>
        </div>
      )
    }

    // Error state
    if (isError) {
      const description =
        error instanceof Error
          ? error.message
          : t('aliases.errors.genericDetail', { defaultValue: 'Unexpected error' })
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-12">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <p className="text-sm text-destructive">{description}</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            {t('messages.retry', { defaultValue: 'Retry' })}
          </Button>
        </div>
      )
    }

    // Table view with subtle vertical separators
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50%] border-r border-border/10">
              {t('aliases.table.headers.externalId', { defaultValue: 'External ID' })}
            </TableHead>
            <TableHead className="border-r border-border/10">
              {t('aliases.table.headers.source', { defaultValue: 'Source' })}
            </TableHead>
            <TableHead className="w-[60px] text-right">
              {t('aliases.table.headers.actions', { defaultValue: '' })}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupedAliases.map(({ slug, displaySources, representativeAlias, removed }) => {
            const isDeleting = pendingAliasId === representativeAlias.id
            const isRestoring = pendingUndoId === representativeAlias.id

            return (
              <TableRow key={slug} className={removed ? 'opacity-50' : undefined}>
                <TableCell className="font-mono text-sm border-r border-border/10">
                  {removed ? (
                    <span className="line-through text-muted-foreground">{slug}</span>
                  ) : (
                    slug
                  )}
                </TableCell>
                <TableCell className="border-r border-border/10">
                  <div className="flex flex-wrap gap-1">
                    {displaySources.map((source) => {
                      const badgeLabel = source.replace(/_/g, ' ')
                      return (
                        <Badge
                          key={source}
                          variant="outline"
                          className="text-[0.65rem] uppercase tracking-wide text-muted-foreground"
                        >
                          {badgeLabel}
                        </Badge>
                      )
                    })}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {removed ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={t('aliases.actions.undo', { defaultValue: 'Undo remove' })}
                      onClick={() => handleUndo(representativeAlias)}
                      disabled={isRestoring || createMutation.isPending}
                    >
                      {isRestoring ? (
                        <LoaderCircle className="size-4 animate-spin" />
                      ) : (
                        <Undo2 className="size-4" />
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      aria-label={t('aliases.actions.delete', { defaultValue: 'Remove alias' })}
                      onClick={() => handleDeleteClick(representativeAlias)}
                      disabled={isDeleting || deleteMutation.isPending}
                    >
                      {isDeleting ? (
                        <LoaderCircle className="size-4 animate-spin" />
                      ) : (
                        <X className="size-4" />
                      )}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    )
  }

  // Don't render card if no variant selected or no aliases exist (after loading)
  if (variantId === null) {
    return null
  }

  if (!isLoading && !isError && groupedAliases.length === 0) {
    return null
  }

  return (
    <>
      <section className="flex flex-col rounded-3xl border border-border/15 bg-card">
        <button
          type="button"
          className="w-full px-6 py-5 border-b border-border/15 text-left cursor-pointer hover:bg-muted/5 transition-colors"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t('aliases.titles.list', { defaultValue: 'Matched Product IDs' })}
                </h3>
                <p className="text-xs text-muted-foreground/70 mt-0.5">
                  {t('aliases.titles.subtitle', {
                    defaultValue: 'from Ad platforms and Marketplaces',
                  })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Metrics summary */}
              {!isLoading && !isError && totals && (
                <div className="flex gap-6 text-right">
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground">
                      {t('aliases.metrics.labels.totalRevenue', { defaultValue: 'Matched revenue' })}
                    </span>
                    <Money value={totals.revenue ?? 0} className="text-sm font-medium" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground">
                      {t('aliases.metrics.labels.totalAdSpend', { defaultValue: 'Matched ad spend' })}
                    </span>
                    <Money value={totals.adSpend ?? 0} className="text-sm font-medium" />
                  </div>
                </div>
              )}
              {/* Expand/collapse icon */}
              <div className="text-muted-foreground">
                {isExpanded ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
              </div>
            </div>
          </div>
        </button>
        {isExpanded && <div className="px-6 pb-6">{renderContent()}</div>}
      </section>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!aliasToDelete}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) {
            setAliasToDelete(null)
          }
        }}
      >
        <DialogContent
          className="sm:max-w-lg"
          onEscapeKeyDown={(e) => {
            // Prevent ESC from bubbling up and closing parent dialogs/pages
            e.stopPropagation()
            if (!deleteMutation.isPending) {
              setAliasToDelete(null)
            }
          }}
          onPointerDownOutside={(e) => {
            if (deleteMutation.isPending) {
              e.preventDefault()
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {aliasToDelete?.source === 'canonical'
                ? t('aliases.deleteConfirmationCanonical.title', {
                    alias: aliasToDelete?.slug,
                    variantName: variantName,
                    defaultValue: `Are you sure you want to remove external ID "${aliasToDelete?.slug}" from ${variantName}?`,
                  })
                : t('aliases.deleteConfirmation.title', {
                    alias: aliasToDelete?.slug,
                    variantName: variantName,
                    defaultValue: `Remove external ID "${aliasToDelete?.slug}" from ${variantName}?`,
                  })}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {aliasToDelete?.source === 'canonical'
              ? t('aliases.deleteConfirmationCanonical.description', {
                  alias: aliasToDelete?.slug,
                  defaultValue:
                    'Careful: external ID "{{alias}}" exactly matches the SKU of this variant.\nIf you remove it, revenue and ad spend from this ID will stop counting for this variant.\n\nThose amounts will move to "Unmatched ad spend & revenue" until you assign this ID to another variant in "Needs review" (for whatever mysterious reason 🤔).',
                })
              : t('aliases.deleteConfirmation.description', {
                  defaultValue:
                    'Revenue and ad spend from this ID will no longer be counted for this variant.\n\nThey will appear as "Unmatched ad spend & revenue" until you assign this ID to another variant in "Needs review".',
                })}
          </p>
          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending
                ? t('aliases.actions.deleting', { defaultValue: 'Deleting...' })
                : t('aliases.actions.delete', { defaultValue: 'Remove alias' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
